import { ArrowForward, FilterList } from "@mui/icons-material";
import { Badge, Box, Button, CircularProgress, InputBase, Paper, Tooltip } from "@mui/material";
import { grey } from "../../../colors";
import EUComplianceText from "../../common/EUComplianceText";

// Simple validation method to block the majority of basic nonsense being submitted to the query enhancer
export const isValidQuestion = (question: string) => {
  const text = String(question);
  const words = text.split(" ");
  return text.length > 8 && words.length > 2 && words.some((_word) => _word.length > 2);
};

export interface QuestionInputProps {
  placeholder?: string;
  isDisabled: boolean;
  question: string;
  filterCount?: number;
  hideFilter?: boolean;
  loading?: boolean;
  onQuestionInput: (text: string) => void;
  onSubmit: (question: string) => void;
  onFilterClick?: () => void;
}

export default function QuestionInputBox({
  placeholder,
  question,
  isDisabled,
  filterCount = 0,
  loading = false,
  onQuestionInput,
  hideFilter = false,
  onSubmit,
  onFilterClick,
}: QuestionInputProps) {
  return (
    <Box>
      <Paper
        component="form"
        elevation={1}
        sx={{
          p: "10px 16px",
          display: "flex",
          width: "100%",
          flexDirection: "column",
          borderRadius: "32px",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.05)",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }}
      >
        <Tooltip
          title={
            question && !isValidQuestion(question) ? "Please enter a valid question with 3 or more words" : undefined
          }
        >
          <InputBase
            sx={{ ml: 1, flex: 1, p: "0.5rem", mt: 1 }}
            disabled={isDisabled}
            multiline
            placeholder={placeholder || "Select filters to narrow down the data, then ask a question..."}
            value={question}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && isValidQuestion(question)) {
                // Block page from reloading on "Enter"
                event.preventDefault();
                onSubmit(question);
              }
            }}
            onChange={(event) => {
              onQuestionInput(event.target.value);
            }}
          />
        </Tooltip>
        <Box display="flex" justifyContent="space-between" alignItems="center" margin="0rem 1rem">
          {!hideFilter && (
            <Button
              size="small"
              startIcon={
                <Badge
                  variant="standard"
                  badgeContent={filterCount}
                  color="secondary"
                  sx={{
                    "& .MuiBadge-badge": {
                      color: "white",
                    },
                  }}
                >
                  <FilterList />
                </Badge>
              }
              sx={{ borderRadius: "32px" }}
              onClick={onFilterClick}
            >
              Filter
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {loading ? (
            <CircularProgress sx={{ p: "0.25em", m: "4px" }} />
          ) : (
            !isDisabled && (
              <Box display="flex">
                <Button
                  disableElevation
                  disabled={!question || !isValidQuestion(question)}
                  variant="contained"
                  aria-label="search"
                  sx={{ padding: "8px", margin: "4px", minWidth: "unset", color: grey[700], borderRadius: "50%" }}
                  onClick={() => onSubmit(question)}
                >
                  <ArrowForward />
                </Button>
              </Box>
            )
          )}
        </Box>
      </Paper>
      <EUComplianceText />
    </Box>
  );
}
